import { CommonModule } from '@angular/common';
import { Component, inject, ViewChild } from '@angular/core';
import { FieldType, FieldTypeConfig, FormlyFieldProps } from '@ngx-formly/core';
import { TranslatePipe } from '@ngx-translate/core';
import { Button } from 'primeng/button';
import { PrimeNG } from 'primeng/config';
import { DividerModule } from 'primeng/divider';
import {
  FileRemoveEvent,
  FileUpload,
  FileUploadHandlerEvent,
} from 'primeng/fileupload';
import { FORM_IMPORTS } from '../../_shared/_imports/form.import';
import { StorageService } from '../../_services/storage.service';
import { ProgressSpinnerModule } from 'primeng/progressspinner';

export interface DocumentProps extends FormlyFieldProps {
  emptyLabel: string;
  uploadButtonLabel: string;
  deleteButtonLabel: string;
  chooseButtonLabel: string;
  downloadButtonLabel: string;
  multiple?: boolean;
  maxFileSize?: number;
  accept?: string;
  modello?: any;
}

@Component({
  selector: 'formly-field-document',
  standalone: true,
  imports: [
    FileUpload,
    CommonModule,
    Button,
    ...FORM_IMPORTS,
    TranslatePipe,
    DividerModule,
    ProgressSpinnerModule,
  ],
  template: `
    <p-fileupload
      #fileUpload
      customUpload="true"
      (uploadHandler)="onUploadHandler($event)"
      (onRemove)="onRemoveFile($event)"
      [multiple]="props.multiple ?? false"
      [maxFileSize]="props.maxFileSize ?? 10000000"
      [mode]="'advanced'"
      auto="true"
      [accept]="props.accept"
    >
      <ng-template
        #header
        let-files
        let-chooseCallback="chooseCallback"
        let-clearCallback="clearCallback"
        let-uploadCallback="uploadCallback"
      >
        <div class="flex flex-col flex-wrap gap-1 w-full">
          <div class="ng-star-inserted">{{ props.label }}</div>

          <div class="flex flex-wrap justify-between gap-2">
            <p-button
              (onClick)="choose($event, chooseCallback)"
              icon="fa-solid fa-upload"
              [outlined]="true"
              size="small"
              [label]="
                props.chooseButtonLabel || 'formly.type.document.chooseButton'
                  | translate
              "
            />
            @if (props.modello) {
              <p-button
                class="self-end"
                (onClick)="
                  donwloadFile({
                    id: props.modello.uuid,
                    name: props.modello.name,
                  })
                "
                icon="fa-solid fa-download"
                [variant]="'text'"
                size="small"
                severity="help"
                [label]="
                  props.downloadButtonLabel ||
                    'formly.type.document.downloadButton' | translate
                "
              />
            }
          </div>

          @if (showError) {
            <small
              class="text-[var(--p-form-field-invalid-placeholder-color)]  italic ml-[var(--p-inputtext-padding-x)]"
            >
              <formly-validation-message
                class="ui-message-text font-bold"
                [field]="field"
              ></formly-validation-message>
            </small>
          }
        </div>
      </ng-template>
      <ng-template #empty> </ng-template>
      <ng-template #file></ng-template>

      <ng-template
        #content
        let-files
        let-progress
        let-uploadedFiles="uploadedFiles"
        let-removeFileCallback="removeFileCallback"
        let-removeUploadedFileCallback="removeUploadedFileCallback"
      >
        @if (formControl.value == 'loading') {
          <p-progress-spinner
            ariaLabel="loading"
            [style]="{ width: '40px', height: '40px' }"
          />
        } @else if (formControl.value && formControl.value.length > 0) {
          <div class="flex flex-col gap-2 ml-4">
            @for (file of formControl.value; track file; let i = $index) {
              <div class="flex items-center justify-between w-full mt-1">
                <div
                  class="flex gap-2 items-center"
                  (click)="open(file.file ?? file)"
                >
                  @if (file.objectURL) {
                    <img
                      role="presentation"
                      [alt]="file.name ?? file.file.name"
                      [src]="file.objectURL"
                      width="50"
                      height="100"
                    />
                  } @else {
                    <i class="fa-regular fa-file-lines text-4xl"></i>
                  }
                  <div class="flex flex-col">
                    <div
                      class="paragraph font-bold  text-ellipsis whitespace-nowrap overflow-hidden"
                    >
                      {{ file.name ?? file.file.name }}
                    </div>
                    <div class="text-xs italic">
                      {{ formatSize(file.size ?? file.file.size) }}
                    </div>
                  </div>
                </div>
                <div class="flex">
                  <p-button
                    icon="fas fa-eye"
                    [rounded]="true"
                    [text]="true"
                    severity="success"
                    (click)="open(file.file ?? file)"
                  />
                  <p-button
                    icon="fas fa-times"
                    [rounded]="true"
                    (click)="removeFileCallback($event, file)"
                    [text]="true"
                    severity="danger"
                  />
                </div>
              </div>
              <p-divider></p-divider>
            }
          </div>
        }
      </ng-template>
    </p-fileupload>
  `,
  styles: `
    :host ::ng-deep {
      .p-fileupload-advanced {
        border: 0;
        background: transparent;
        color: inherit;

        .p-fileupload-header {
          padding: 0;
          color: inherit;
        }

        .p-fileupload-content {
          padding: 0;

          p-progressbar {
            display: none;
          }

          p-message {
            margin-top: 0.5rem;
          }
        }
      }
    }
  `,
})
export class DocumentFieldType extends FieldType<
  FieldTypeConfig<DocumentProps>
> {
  uploadedFiles: any[] = [];

  @ViewChild('fileUpload') fileUpload!: FileUpload;

  private readonly _storage = inject(StorageService);

  constructor(private config: PrimeNG) {
    super();
  }

  choose(event: any, callback: any) {
    callback();
  }

  uploadEvent(callback: any) {
    callback();
  }

  onUploadHandler(event: FileUploadHandlerEvent) {
    this.formControl.setValue(event.files);
  }

  onRemoveFile(event: FileRemoveEvent) {
    setTimeout(() => {
      this.formControl.setValue(this.fileUpload.files);
    });
  }

  formatSize(bytes: number) {
    const k = 1024;
    const dm = 2;
    const sizes = this.config.translation.fileSizeTypes;
    if (bytes === 0) {
      return `0 ${sizes![0]}`;
    }

    const i = Math.floor(Math.log(bytes) / Math.log(k));
    const formattedSize = parseFloat((bytes / Math.pow(k, i)).toFixed(dm));

    return `${formattedSize} ${sizes![i]}`;
  }

  open(file: any) {
    const fileURL = URL.createObjectURL(file);
    window.open(fileURL, '_blank');
  }

  donwloadFile(file: any) {
    if (file.id && file.name)
      this._storage.getFile(file.id, file.name).subscribe((res) => {
        const url = URL.createObjectURL(res?.file);
        const a = document.createElement('a');
        a.href = url;
        a.download = file.name!;
        a.click();
        URL.revokeObjectURL(url);
      });
  }

  private fileCache = new Map<File, string>(); // Cache per evitare ricaricamenti

  render(file: File): string {
    if (this.fileCache.has(file)) {
      return this.fileCache.get(file)!; // Se già caricato, restituisci il valore
    }

    const reader = new FileReader();
    reader.readAsDataURL(file);

    reader.onload = () => {
      this.fileCache.set(file, reader.result as string); // Memorizza il valore nella cache
    };

    return ''; // Restituisce una stringa vuota finché l'immagine non è caricata
  }
}
